import { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { stringify } from '../../util/urlHelpers';
import { createResourceLocatorString } from '../../util/routes';
import routeConfiguration from '../../routeConfiguration';
import categoryRoutesConfiguration from '../../categoryRoutes';
import {
  parse,
  encodeLatLngBounds,
} from '../../util/urlHelpers';

const STORAGE_KEYS = {
  WHAT: 'recent_searches_what',
  WHERE: 'recent_searches_where',
};

const TOTAL_NUMBER_OF_RECENT_SEARCHES = 3;
const NUMBER_OF_DEGREES_FROM_ORIGIN = 0.003;

const getStorageItems = key => {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : [];
  } catch (e) {
    console.error(`Błąd odczytu z localStorage dla klucza ${key}:`, e);
    return [];
  }
};

const saveStorageItems = (key, items) => {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(key, JSON.stringify(items));
  } catch (e) {
    console.error(`Błąd zapisu do localStorage dla klucza ${key}:`, e);
  }
};

const getInitialValues = search => {
  const params = new URLSearchParams(search);
  const startDateStr = params.get('start');
  const endDateStr = params.get('end');

  const parseShortDate = dateStr => {
    if (!dateStr) return null;
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const originParam = params.get('origin');
  let initialOrigin = null;
  if (originParam) {
    const [lat, lng] = originParam.split(',').map(Number);
    if (!isNaN(lat) && !isNaN(lng)) {
      initialOrigin = { lat, lng };
    }
  }

  return {
    what: params.get('keywords') || '',
    where: params.get('address') || '',
    category: params.get('category') || '',
    subcategory: params.get('businessCategory') || '',
    exact: params.get('exact') === 'true',
    startDate: parseShortDate(startDateStr),
    endDate: parseShortDate(endDateStr),
    selectedPlace: initialOrigin ? { origin: initialOrigin, bounds: null } : null,
  };
};

const toLocalShortDate = date => {
  if (!date) return null;
  const offset = date.getTimezoneOffset() * 60000;
  const localDate = new Date(date.getTime() - offset);
  return localDate.toISOString().split('T')[0];
};

const SEARCH_PAGE = 'SearchPage';
const configCountryId = process.env.REACT_APP_COUNTRY_ID;
const categoryRoutes = categoryRoutesConfiguration();
const supportedCategoryRoutes = categoryRoutes.filter(cr => cr.countries.includes(configCountryId));

const getNextEmptySection = data => {
  if (!data.what) return 'what';
  if (!data.where) return 'where';
  if (!data.startDate || !data.endDate) return 'when';
  return null;
};

export const useSearchBar = pageName => {
  const location = useLocation();
  const { providerId, countryId, homeDelivery, sort }  = parse(location.search);
  const history = useHistory();
  const initialValues = useMemo(() => getInitialValues(location.search), [location.search]);
  const [recentWhat, setRecentWhat] = useState(() => getStorageItems(STORAGE_KEYS.WHAT));
  const [recentWhere, setRecentWhere] = useState(() => getStorageItems(STORAGE_KEYS.WHERE));
  const [formData, setFormData] = useState(initialValues);
  const [activeSection, setActiveSection] = useState(null);

  const timeoutRef = useRef(null);
  const searchBarRef = useRef(null);

  const handleDateSelect = useCallback((start, end) => {
    setFormData(prev => {
      const nextData = {
        ...prev,
        startDate: start,
        endDate: end,
      };
      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      if (start && end) {
        const nextSection = getNextEmptySection(nextData);
        timeoutRef.current = setTimeout(() => {
          setActiveSection(nextSection);
        }, 300);
      }
      return nextData;
    });
  }, []);

  const handleLocationSelect = useCallback(locationData => {
    setFormData(prev => {
      const nextData = {
        ...prev,
        where: locationData.location,
        selectedPlace: {
          origin: locationData.origin,
          bounds: locationData.bounds,
        },
      };
      setActiveSection(getNextEmptySection(nextData));
      return nextData;
    });
  }, []);

  const handleCategorySelect = useCallback(categoryData => {
    setFormData(prev => {
      const nextData = {
        ...prev,
        what: categoryData.keyword,
        category: categoryData.category,
        subcategory: categoryData.subcategory,
        exact: categoryData.exact,
      };
      setActiveSection(getNextEmptySection(nextData));
      return nextData;
    });
  }, []);

  const handleWhatInputChange = useCallback(val => {
    setFormData(prev => ({ ...prev, what: val }));
  }, []);

  const handleLocationInputChange = useCallback(val => {
    setFormData(prev => ({
      ...prev,
      where: val,
      selectedPlace: val ? prev.selectedPlace : null,
    }));
  }, []);

  const handleClear = useCallback(
    (e, data, section, params) => {
      e.stopPropagation();
      setFormData(prev => ({ ...prev, ...data }));
      setActiveSection(section);
    },
    [history, location.search, pageName]
  );

  const handleClearWhat = useCallback(
    (e, targetSection = 'what') => {
      handleClear(
        e,
        {
          what: '',
          category: '',
          subcategory: '',
          exact: false,
        },
        targetSection,
        {
          keywords: undefined,
          category: undefined,
          businessCategory: undefined,
          exact: undefined,
        }
      );
    },
    [handleClear]
  );

  const handleClearWhere = useCallback(
    (e, targetSection = 'where') => {
      handleClear(e, { where: '', selectedPlace: null }, targetSection, {
        address: undefined,
        origin: undefined,
      });
    },
    [handleClear]
  );

  const handleClearDates = useCallback(
    (e, targetSection = 'when') => {
      handleClear(e, { startDate: null, endDate: null }, targetSection, {
        start: undefined,
        end: undefined,
      });
    },
    [handleClear]
  );

  const handleClearAll = useCallback(
    e => {
      handleClear(
        e,
        {
          what: '',
          category: '',
          subcategory: '',
          where: '',
          selectedPlace: null,
          exact: false,
          startDate: null,
          endDate: null,
        },
        'what',
        {
          keywords: undefined,
          category: undefined,
          businessCategory: undefined,
          exact: undefined,
          address: undefined,
          origin: undefined,
          start: undefined,
          end: undefined,
        }
      );
    },
    [handleClear]
  );

  const handleClearRecentWhat = useCallback(e => {
    if (e) e.stopPropagation();
    try {
      localStorage.removeItem(STORAGE_KEYS.WHAT);
    } catch (err) {
      console.error('Błąd podczas czyszczenia historii wyszukiwań:', err);
    }
    setRecentWhat([]);
  }, []);

  const handleClearRecentWhere = useCallback(e => {
    if (e) e.stopPropagation();
    try {
      localStorage.removeItem(STORAGE_KEYS.WHERE);
    } catch (err) {
      console.error('Błąd podczas czyszczenia historii wyszukiwań lokalizacji:', err);
    }
    setRecentWhere([]);
  }, []);

  const handleSubmit = useCallback(
    e => {
      if (e) e.preventDefault();
      const {
        what,
        category,
        subcategory,
        exact,
        where,
        selectedPlace,
        startDate,
        endDate,
      } = formData;
      const origin = selectedPlace?.origin;
      const bounds = selectedPlace?.bounds;

      if (what || subcategory || category) {
        const searchKeyword = what || '';
        const searchCategory = subcategory || category || '';
        const currentWhatList = getStorageItems(STORAGE_KEYS.WHAT);

        const filteredWhatList = currentWhatList.filter(item => {
          const itemKeyword = item.what || item.keyword || '';
          const itemCategory = item.subcategory || item.category || '';

          if (searchCategory && itemCategory) {
            return itemCategory !== searchCategory;
          }
          return itemKeyword.toLowerCase() !== searchKeyword.toLowerCase();
        });

        const newWhatItem = {
          what: searchKeyword,
          keyword: searchKeyword,
          category: category || '',
          subcategory: subcategory || '',
          exact: !!exact,
        };

        const updatedWhatList = [newWhatItem, ...filteredWhatList].slice(
          0,
          TOTAL_NUMBER_OF_RECENT_SEARCHES
        );

        saveStorageItems(STORAGE_KEYS.WHAT, updatedWhatList);
        setRecentWhat(updatedWhatList);
      }

      if (where) {
        const originStr = origin
          ? typeof origin === 'string'
            ? origin
            : `${origin.lat},${origin.lng}`
          : null;

        const currentWhereList = getStorageItems(STORAGE_KEYS.WHERE);

        const filteredWhereList = currentWhereList.filter(
          item => (item.location || item.where || '').toLowerCase() !== where.toLowerCase()
        );

        const [lat, lng] = originStr.split(',').map(Number);

        const calculatedBounds = originStr
          ? `${lat + NUMBER_OF_DEGREES_FROM_ORIGIN},${lng + NUMBER_OF_DEGREES_FROM_ORIGIN},${lat -
              NUMBER_OF_DEGREES_FROM_ORIGIN},${lng - NUMBER_OF_DEGREES_FROM_ORIGIN}`
          : '';
        const boundsValue = bounds && !!Object.keys(bounds).length ? encodeLatLngBounds(bounds) : calculatedBounds;
        const newWhereItem = {
          location: where,
          origin: originStr,
          bounds: boundsValue || null,
        };

        const updatedWhereList = [newWhereItem, ...filteredWhereList].slice(
          0,
          TOTAL_NUMBER_OF_RECENT_SEARCHES
        );

        saveStorageItems(STORAGE_KEYS.WHERE, updatedWhereList);
        setRecentWhere(updatedWhereList);
      }

      const foundSupportedCategory = supportedCategoryRoutes.find(scr => scr.key === subcategory);

      const targetPathname = foundSupportedCategory
        ? createResourceLocatorString(foundSupportedCategory.name, routeConfiguration(), {}, {})
        : createResourceLocatorString(pageName || SEARCH_PAGE, routeConfiguration(), {}, {});

      const originMaybe = origin ? { origin: `${origin.lat},${origin.lng}` } : {};
      const addressMaybe = where ? { address: where } : {};
      const categoryMaybe = category && !foundSupportedCategory ? { category } : {};
      const businessCategoryMaybe =
        subcategory && !foundSupportedCategory ? { businessCategory: subcategory } : {};
      const exactMaybe = exact && !foundSupportedCategory ? { exact: true } : {};
      const keywordsMaybe = what && !exact ? { keywords: what } : {};

      const datesMaybe =
        startDate && endDate
          ? {
              start: toLocalShortDate(startDate),
              end: toLocalShortDate(endDate),
            }
          : {};

      const searchParams = {
        ...originMaybe,
        ...addressMaybe,
        ...categoryMaybe,
        ...businessCategoryMaybe,
        ...keywordsMaybe,
        ...exactMaybe,
        ...datesMaybe,
        providerId, countryId, homeDelivery, sort 
      };

      history.push({
        pathname: targetPathname,
        search: stringify(searchParams),
        state: { bounds },
      });
      setActiveSection(null);
    },
    [formData, history, pageName]
  );

  useEffect(() => {
    setFormData(getInitialValues(location.search));
  }, [location.search]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  return {
    formData,
    activeSection,
    setActiveSection,
    searchBarRef,
    recentWhat,
    recentWhere,
    handleDateSelect,
    handleLocationSelect,
    handleCategorySelect,
    handleWhatInputChange,
    handleLocationInputChange,
    handleClearWhat,
    handleClearWhere,
    handleClearDates,
    handleClearAll,
    handleSubmit,
    handleClearRecentWhat,
    handleClearRecentWhere,
  };
};
